// A group's section nav, shared by every screen inside a group.
//
// Modelled on adminNav.ts, and extracted for the same reason: this list used to
// be hand-rolled in five views, which had already drifted — the events tab
// offered "Repeating" and no Photos-adjacent Feed, photos offered no Feed at
// all, and the group landing page was the only screen listing everything. A
// member's route through a group depended on which door they came in.
//
// Feed is deliberately FIRST and points at `/groups/:id`, the group's home. See
// docs/plan-feed-as-home.md: landing on a group puts you in its conversation,
// and the roster it used to show now lives behind Members.

import { el } from '../dom.js';
import { EVENT_TYPES, type EventTypeKey } from '@msc/shared';

/**
 * Does this group run fasts?
 *
 * ⚠ Read off `allowedEventTypes` rather than a new per-group flag, because the
 * group ALREADY says which activities it does and inventing a second switch for
 * the same idea is how two sources of truth start. `null` means the group has
 * not narrowed its list, which means every type is allowed -- so the tab shows.
 * A poker-only group never sees it.
 *
 * ⚠ The RETIRED key `duration` counts too. A group that narrowed its list
 * before the leaf split stored `duration`, and that group is a fasting group --
 * matching only `fasting` would hide the tab from exactly the groups that have
 * been doing this longest. Same reason `eventTypesAutoClose()` deliberately
 * still returns retired keys.
 */
export function groupRunsFasts(allowedEventTypes: EventTypeKey[] | null | undefined): boolean {
  if (allowedEventTypes === null || allowedEventTypes === undefined) return true;
  if (allowedEventTypes.length === 0) return true;
  return allowedEventTypes.some((k) => k === 'fasting' || (k as string) === 'duration');
}

/**
 * Does this group have anything to put on a leaderboard?
 *
 * The board counts wins from `event_results`, which only a type with
 * `hasResults` writes. A group whose allowed types are all resultless (a Bible
 * study, a workout group) would show a permanently empty "Leaderboard" tab, and
 * for a faith group it is simply the wrong idea. Read off the registry flag, not
 * a family name, so a future leaf with results inherits the tab for free.
 * `null` or an empty list means no narrowing, so every type is allowed.
 */
export function groupShowsLeaderboard(allowedEventTypes: EventTypeKey[] | null | undefined): boolean {
  if (allowedEventTypes === null || allowedEventTypes === undefined) return true;
  if (allowedEventTypes.length === 0) return true;
  return allowedEventTypes.some((k) => EVENT_TYPES[k]?.hasResults === true);
}

// Calendar and Repeating used to be their own tabs. They are now reached from
// inside Events (a List/Calendar toggle, and a Repeats link for organizers) —
// see events.ts — so this list only carries one Events entry.
export type GroupSection =
  | 'feed'
  | 'events'
  | 'leaderboard'
  | 'photos'
  | 'members'
  | 'fasting'
  | 'manage';

const SECTIONS: Array<{ key: GroupSection; path: string; label: string }> = [
  // The group home. No suffix — the feed IS `/groups/:id`.
  { key: 'feed', path: '', label: 'Feed' },
  { key: 'events', path: '/events', label: 'Events' },
  { key: 'leaderboard', path: '/leaderboard', label: 'Leaderboard' },
  { key: 'photos', path: '/photos', label: 'Photos' },
  { key: 'members', path: '/members', label: 'Members' },
];

/**
 * `canManage` appends a Manage tab. It used to live as a one-off button on the
 * Members page only, which meant an admin had to know to go there first. Every
 * screen fetches `group` already, so every screen can pass its own
 * `group.myRole >= GroupRole.ADMIN` here instead.
 */
export function groupSubnav(
  groupId: number,
  active: GroupSection,
  canManage = false,
  /**
   * Whether to offer the Fasting tab.
   *
   * ⚠ Off by default, and gated on the group's OWN capability rather than on a
   * new flag: a group whose `allowedEventTypes` includes `fasting` (or which has
   * not narrowed the list at all) is a group that runs fasts. Reusing that
   * mechanism means a poker group never sees a Fasting tab and nobody had to
   * invent a second switch for the same idea.
   *
   * Passed rather than derived here because this module has no access to the
   * group -- every caller already fetches it.
   */
  showFasting = false,
  /** Hide the Leaderboard tab for groups with no type that records results. */
  showLeaderboard = true,
): HTMLElement {
  const base = showLeaderboard ? SECTIONS : SECTIONS.filter((s) => s.key !== 'leaderboard');
  const sections = showFasting
    ? [...base, { key: 'fasting' as GroupSection, path: '/fasting', label: 'Fasting' }]
    : base;

  const links = sections.map((s) => {
    const href = `/groups/${groupId}${s.path}`;
    // aria-current, not just a class: the active section has to be announced,
    // not only coloured.
    return el(
      'a',
      s.key === active
        ? { href, class: 'active', 'aria-current': 'page' }
        : { href },
      s.label,
    );
  });

  if (canManage) {
    const href = `/groups/${groupId}/manage`;
    links.push(
      el(
        'a',
        active === 'manage' ? { href, class: 'active', 'aria-current': 'page' } : { href },
        'Manage',
      ),
    );
  }

  return el('nav', { class: 'subnav' }, links);
}
